import moment from 'moment';
import { ShiftFactory } from './shifts';

const TWENTY_YEARS_FROM_TODAY = moment().utc().add(20, 'years');

export class ShiftOverlapFinder {
  constructor(ruleOne, ruleTwo, logger = console) {
    this.shiftOne = ShiftFactory(ruleOne);
    this.shiftTwo = ShiftFactory(ruleTwo);
    this.logger = logger;
  }

  run() {
    return (this.shiftsShareTime() && this.shiftsShareDays())
      || this.overlapWhenShiftPassesMidnight(this.shiftOne, this.shiftTwo)
      || this.overlapWhenShiftPassesMidnight(this.shiftTwo, this.shiftOne);
  }

  firstOverlappingDate() {
    const date = this.shiftOne.startsOn;
    while (date <= TWENTY_YEARS_FROM_TODAY) {
      if (this.shiftTwo.canFallOnDate(date)) { return date; }
      this.shiftOne.incrementDate(date);
      if (this.neitherShiftFinalDateAfter(date)) { return false; }
    }
    this.logger.log('Date out of range: %j', date.toString());
    return false;
  }

  neitherShiftFinalDateAfter(date) {
    return !this.shiftOne.finalDateAfter(date) || !this.shiftTwo.finalDateAfter(date);
  }

  overlapWhenShiftPassesMidnight(shiftOne, shiftTwo) {
    if (shiftOne.minutesPastEndOfDay > 0) {
      const shiftOneRules = shiftOne.rulesForMinutesAfterMidnight();
      return new ShiftOverlapFinder(shiftOneRules, shiftTwo.rule).run();
    }
    return false;
  }

  shiftsAreByMonth() {
    return this.shiftOne.monthdays && this.shiftTwo.monthdays;
  }

  shiftsAreByWeeks() {
    return this.shiftOne.weeks && this.shiftTwo.weeks;
  }

  shiftsAreByWeekday() {
    return this.shiftOne.weekdays && this.shiftTwo.weekdays;
  }

  shiftsHaveCollidingIntervals() {
    if (!this.shiftsHaveParallelIntervals()) { return true; }
    return this.shiftOne.hasCollidingIntervalWithShift(this.shiftTwo);
  }

  shiftsHaveParallelIntervals() {
    return this.shiftsHaveSameType() && this.shiftIntervalsShareDenominator();
  }

  shiftsHaveSameType() {
    return this.shiftOne.constructor.name === this.shiftTwo.constructor.name;
  }

  shiftIntervalsShareDenominator() {
    return this.shiftOne.interval % this.shiftTwo.interval === 0 || this.shiftTwo.interval % this.shiftOne.interval === 0;
  }

  shiftRangesOverlap() {
    const firstDate = this.shiftsHaveCollidingIntervals() && this.firstOverlappingDate();
    return firstDate && this.shiftOne.finalDateAfter(firstDate) && this.shiftTwo.finalDateAfter(firstDate);
  }

  shiftsShareDays() {
    if (this.shiftsAreByWeeks() && !this.shiftsShareWeeks()) { return false; }
    if (this.shiftsAreByWeekday() && !this.shiftsShareWeekdays()) { return false; }
    if (this.shiftsAreByMonth() && !this.shiftsShareMonthdays()) { return false; }
    return this.shiftRangesOverlap();
  }

  shiftsShareMonthdays() {
    const intersect = this.shiftOne.monthdays.filter(date => this.shiftTwo.monthdays.indexOf(date) !== -1);
    return intersect.length > 0;
  }

  shiftsShareTime() {
    return this.shiftOne.startTimeWithinRange(this.shiftTwo.startTime, this.shiftTwo.endTime)
      || this.shiftTwo.startTimeWithinRange(this.shiftOne.startTime, this.shiftOne.endTime)
      || this.shiftOne.endMinutesWithinRange(this.shiftTwo.startTime, this.shiftTwo.endTime)
      || this.shiftTwo.endMinutesWithinRange(this.shiftOne.startTime, this.shiftOne.endTime);
  }

  shiftsShareWeekdays() {
    const intersect = this.shiftOne.weekdays.filter(day => this.shiftTwo.weekdays.indexOf(day) !== -1);
    return intersect.length > 0;
  }

  shiftsShareWeeks() {
    const intersect = this.shiftOne.weeks.filter(day => this.shiftTwo.weeks.indexOf(day) !== -1);
    return intersect.length > 0;
  }
}
